import React from 'react'
import { Button, Container, Typography } from '@mui/material'
import { CLIENT_URL, SITE_NAME } from '../../../../constants'
import { Link } from 'react-router-dom'
import { Fade } from 'react-awesome-reveal'
import { getCategoryUrl, getProductsDataSheetDownloadUrl, getWholesaleProductUrl } from '../../../urls'
import { WholesaleProductsQueryResult } from '@graphql/queries/__generated__/wholesale.generated'
import { UserFragment } from '@graphql/fragments/__generated__/User.generated'
import { WholesaleAppStatusEnum } from '../../../../generated/gqlTypes'
import { Trans, useTranslation } from 'react-i18next'

interface Section {
  id: string
  title: string
  description: string
  image: string
  url: string
  order: number
  resultFile: string | null
  categoryUrl: string | null
}

interface Props {
  products: WholesaleProductsQueryResult['data']['wholesaleProducts']['edges'][0]['node'][]
  user: UserFragment | null
}

const Page: React.FC<Props> = ({ products, user }) => {
  const { t } = useTranslation()
  const SECTIONS: Section[] = products.map((p, idx) => ({
    id: p.id,
    title: p.name,
    url: getWholesaleProductUrl(p.slug),
    order: idx % 2 ? 2 : 1,
    description: p.description?.description1 || '',
    image: p.squareImage,
    resultFile: p.category?.productsDataFile?.resultFile
      ? getProductsDataSheetDownloadUrl(p.category.productsDataFile.pk)
      : null,
    categoryUrl: p.category ? getCategoryUrl(p.category.slug, p.category.pk) : null,
  }))
  const isUserWholesale = user?.wholesaleApp?.status === WholesaleAppStatusEnum.Confirmed
  const isStaff = user?.isStaff
  return (
    <div>
      <section style={{ backgroundColor: '#f2eeea' }}>
        <Container maxWidth='lg'>
          <div className='text-center min-h-200 md:min-h-300 flex justify-center items-center flex-col py-20'>
            <Typography className='capitalize' variant='h3' marginBottom={1}>
              <span className='text-4xl md:text-5xl'>
                {t('wholesale.siteProductCategories', {
                  defaultValue: '{{siteName}} product categories',
                  siteName: SITE_NAME,
                })}
              </span>
            </Typography>
            <Typography variant='body1'>
              <Trans i18nKey='wholesale.productsDescription'>
                Awesome Pattern Studio products are designed by talented artists in Ukraine for cross-stitch, beading,
                diamond painting, hand embroidery, and machine embroidery projects. Explore our categories, filter by
                the degree of difficulty, and find a great variety of themes including cities, national parks, animals,
                nature-even the human body. From modern to traditional, abstract to funny, Awesome Pattern Studio
                creates something for everyone. Check with us often since we are constantly adding new designs!
              </Trans>
            </Typography>
          </div>
        </Container>
      </section>

      <section className='overflow-hidden'>
        <Container maxWidth='lg' disableGutters>
          {SECTIONS.map(node => {
            const orderImageClasses = `order-1 md:order-${node.order}`
            const orderDescriptionClasses = `order-1 md:order-${node.order - 1}`
            const direction = node.order === 1 ? 'right' : 'left'
            return (
              <div key={node.id} className={`flex flex-col md:flex-row items-center`}>
                <div className={`w-full md:w-1/2 pb-full md:pb-half relative ${orderImageClasses}`}>
                  <div className='absolute inset-0'>
                    <Link to={node.url}>
                      <img className='max-w-full' src={node.image} alt={node.title} />
                    </Link>
                  </div>
                </div>
                <div className={`w-full md:w-1/2 text-center mt-30 mb-60 px-10 md:px-60 ${orderDescriptionClasses}`}>
                  <Fade triggerOnce direction={direction}>
                    <Link to={node.url}>
                      <Typography variant='h3' marginBottom={2}>
                        <span className='text-4xl md:text-5xl'>{node.title}</span>
                      </Typography>
                    </Link>
                    <Typography variant='body1' component='div'>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: node.description,
                        }}
                      />
                    </Typography>
                    <div className='flex items-center justify-center flex-wrap mt-10'>
                      {(isUserWholesale || isStaff) && node.resultFile && (
                        <span className='mr-0 sm:mr-10 mb-10 w-full sm:w-auto'>
                          <Button
                            variant='outlined'
                            size='large'
                            href={node.resultFile}
                            target='_blank'
                            fullWidth
                            download
                          >
                            <span className='capitalize'>
                              {t('button.downloadProductsData', 'Download Products Data')}
                            </span>
                          </Button>
                        </span>
                      )}
                      <span className='mb-10 w-full sm:w-auto'>
                        <Button variant='outlined' size='large' component={Link} to={node.url} fullWidth>
                          <span className='capitalize'>{t('button.readMore', 'Read More')}</span>
                        </Button>
                      </span>
                    </div>
                  </Fade>
                </div>
              </div>
            )
          })}
        </Container>
      </section>
    </div>
  )
}

export default Page
